import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase, PRODUCT } from './supabase';
import { DEMO_PATIENTS } from './demo-data';
import type {
  DashboardStats,
  DemoPatient,
  PracticeBrandingForm,
  PracticeContext,
  WaitlistFamily,
} from './types';

function waitlistToPatient(f: WaitlistFamily): DemoPatient {
  const label = f.name?.trim() || (f.email.split('@')[0] ?? 'Família');
  return {
    id: `wl-${f.id}`,
    name: label.startsWith('Pequeno') ? label : `Família ${label}`,
    age: 'Waitlist',
    parents: f.name ?? f.email,
    photo: '👪',
    weight: 0,
    height: 0,
    formula: '—',
    lastSeen: new Date(f.createdAt).toLocaleDateString('pt-BR'),
    alert: f.status === 'pending',
    alertMsg: f.status === 'pending' ? 'Aguardando abertura do beta' : null,
    source: 'waitlist',
    waitlistId: f.id,
  };
}

export function usePracticeContextQuery(enabled: boolean) {
  return useQuery({
    queryKey: ['practice-context', PRODUCT],
    enabled,
    queryFn: async (): Promise<PracticeContext | null> => {
      const { data, error } = await supabase.rpc('get_my_practice_context', {
        p_product: PRODUCT,
      });
      if (error) throw error;
      if (!data?.practice) return null;
      return {
        memberRole: data.memberRole,
        practice: data.practice,
      };
    },
  });
}

export function useDashboard() {
  return useQuery({
    queryKey: ['practice-dashboard', PRODUCT],
    queryFn: async (): Promise<DashboardStats> => {
      const { data, error } = await supabase.rpc('get_practice_dashboard', {
        p_product: PRODUCT,
      });
      if (error) throw error;
      if (!data) {
        return {
          waitlistTotal: 0,
          waitlistLast7d: 0,
          waitlistLast24h: 0,
          demoPatientsCount: DEMO_PATIENTS.length,
          alertsCount: 1,
          appointmentsToday: 2,
        };
      }
      return data as DashboardStats;
    },
  });
}

export function useWaitlistFamilies() {
  return useQuery({
    queryKey: ['practice-waitlist-families', PRODUCT],
    queryFn: async (): Promise<WaitlistFamily[]> => {
      const { data, error } = await supabase.rpc('list_practice_waitlist_families', {
        p_product: PRODUCT,
      });
      if (error) throw error;
      return (data ?? []) as WaitlistFamily[];
    },
  });
}

export function usePatients() {
  const waitlist = useWaitlistFamilies();
  return useQuery({
    queryKey: ['practice-patients', PRODUCT, waitlist.data],
    enabled: waitlist.isSuccess,
    queryFn: async (): Promise<DemoPatient[]> => {
      const fromWaitlist = (waitlist.data ?? []).map(waitlistToPatient);
      const seen = new Set(fromWaitlist.map((p) => p.id));
      const demo = DEMO_PATIENTS.filter((p) => !seen.has(p.id));
      return [...fromWaitlist, ...demo];
    },
  });
}

export function useSaveBranding() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (form: PracticeBrandingForm & { id: string }) => {
      const { error } = await supabase
        .from('practices')
        .update({
          display_name: form.display_name,
          doctor_name: form.doctor_name || null,
          crm: form.crm || null,
          tagline: form.tagline || null,
          primary_color: form.primary_color || null,
          logo_url: form.logo_url || null,
          updated_at: new Date().toISOString(),
        })
        .eq('id', form.id);
      if (error) throw error;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['practice-context'] });
    },
  });
}
